import { useMemo } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export interface GroupedOption<T> {
  /** the actual value you care about */
  value: T;
  /** what shows up in the list */
  label: string;
  /** which group/section this belongs to */
  group: string;

  /** optionally used for comparison */
  [key: string]: any;
}

export interface GroupedSelectProps<T> {
  /** the currently selected value (or null for none) */
  value: T | null;
  /** called when the user picks something new (or clears) */
  onChange: (newValue: T | null) => void;
  /** the list of all options */
  options: GroupedOption<T>[];
  /** input placeholder/textfield label */
  placeholder?: string;
  /** disable the control */
  disabled?: boolean;
  /** show loading spinner */
  loading?: boolean;
  /** custom equality checker (optional) */
  isEqual?: (opt: GroupedOption<T>, val: GroupedOption<T>) => boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler;
}

export function GroupedSelect<T>(
  {
    value,
    onChange,
    options,
    placeholder = '',
    disabled = false,
    loading = false,
    isEqual = (a, b) => a.value === b.value,
    inputRef,
    onKeyDown
  }: GroupedSelectProps<T>) {
  const { normalizedSortedOptions, labelByKey } = useMemo(() => {
    const map = new Map<string, string>();
    const list = options.map(o => {
      const rawGroup = (o.group ?? '');
      const display = rawGroup.trim();
      const key = display.toLowerCase();
      if (!map.has(key)) map.set(key, display);
      return { ...o, __groupKey: key };
    });
    list.sort((a, b) => {
      if (a.__groupKey === b.__groupKey) {
        return String(a.label ?? '').localeCompare(String(b.label ?? ''), undefined, { sensitivity: 'base' });
      }
      return a.__groupKey.localeCompare(b.__groupKey, undefined, { sensitivity: 'base' });
    });
    return { normalizedSortedOptions: list, labelByKey: map };
  }, [options]);

  const selectedOption = useMemo(
    () => {
      return normalizedSortedOptions.find(opt => isEqual(opt, { value } as GroupedOption<T>)) || normalizedSortedOptions.find(o => o.value === value);
    },
    [isEqual, normalizedSortedOptions, value]
  );

  return (
    <Autocomplete
      className="w-full"
      size="small"
      disabled={disabled}
      loading={loading}
      options={normalizedSortedOptions}
      value={selectedOption ?? null}
      groupBy={(opt) => opt.__groupKey}
      getOptionLabel={(opt) => opt.label}
      getOptionKey={(opt) => `${opt.__groupKey}-${opt.value}`}
      isOptionEqualToValue={isEqual}

      filterOptions={(opts, state) => {
        const q = (state.inputValue ?? '').toString().trim().toLowerCase();
        if (!q) return opts;
        return opts.filter(o => {
          const label = (o.label ?? '').toString().toLowerCase();
          const group = (o.group ?? '').toString().toLowerCase();
          return label.includes(q) || group.includes(q);
        });
      }}
      onChange={(_, newOpt) => {
        onChange(newOpt ? newOpt.value : null);
      }}
      renderGroup={(params) => (
        <div key={params.key}>
          <div className="bg-theme-info px-3 py-1 text-sm font-semibold text-white">
            {labelByKey.get(params.group) ?? params.group}
          </div>
          <ul className="divide-y divide-blue-100">
            {params.children}
          </ul>
        </div>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={selectedOption ? selectedOption.group : placeholder}
          fullWidth
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              inputRef: inputRef,
              onKeyDown: onKeyDown,
              endAdornment: (
                <>
                  {loading && (
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2 text-blue-500" />
                  )}
                  {params.slotProps.input.endAdornment}
                </>
              )
            }
          }}
        />
      )}
    />
  );
}
